// utils/validation.js

import { toast } from "./toast";
import { filterAvailableRides, filterAvailableShows } from "./availabilityRules";

// Human-friendly field labels
const friendlyLabelMap = {
  fullName: "Your full name",
  email: "Your email address",
  phoneNumber: "Your phone number",
  startDate: "Trip start date",
  endDate: "Trip end date",
  adults: "Number of adults",
  numAdultMales: "Number of adult males",
  numAdultFemales: "Number of adult females",
  kids: "Number of children",
  numBoys: "Number of boys",
  numGirls: "Number of girls",
  agesOfBoys: "Ages of boys",
  agesOfGirls: "Ages of girls",
  ticketType: "Your Disney ticket type",
  hasParkHopper: "Park Hopper or Annual Pass option",
  parks: "Selected parks",
  mkDays: "Magic Kingdom day count",
  hsDays: "Hollywood Studios day count",
  epcotDays: "EPCOT day count",
  akDays: "Animal Kingdom day count",
  tlDays: "Typhoon Lagoon day count",
  bbDays: "Blizzard Beach day count",
  stayingOnProperty: "Resort stay preference",
  resort: "Disney resort name",
  offPropertyHotel: "Off-property hotel name",
  otherHotel: "Specific off-property hotel",
  rideStrategy: "Your ride strategy",
  lightningLane: "Lightning Lane preference",
  mustRides: "Your top ride selections",
  showStrategy: "Show & entertainment interests",
  mustSeeShows: "Must-see shows",
  diningPlanInterest: "Interest in dining plan",
  dining: "Dining preferences",
  mustTryRestaurants: "Your restaurant selections",
  diningTime: "Preferred dining time",
  cuisinePreferences: "Cuisine preferences",
  allergies: "Food allergies",
  parkStartTime: "Preferred park entry time",
  breakTime: "Midday break plans",
  celebration: "Whether you're celebrating anything special",
  celebrationDetails: "What you're celebrating",
  celebrationDate: "Date of the celebration",
  hasDisability: "Disability or mobility concerns",
  disabilities: "Disability notes",
  additionalInfo: "Additional trip notes",
  transportation: "Transportation method",
  restaurantStrategy: "Your restaurant preferences",
  enchantedExperiences: "Interest in enchanted experiences",
  mustExperience: "Premium experience selections",
};

// Optional field IDs
const optionalIds = [
  "agesOfBoys",
  "agesOfGirls",
  "additionalInfo",
  "otherRides",
  "endDate",
  "disabilities",
  "otherHotel",
];

// Utility
const isEmpty = (val) =>
  val === undefined ||
  val === null ||
  (typeof val === "string" && val.trim() === "") ||
  (Array.isArray(val) && val.length === 0);

// Park mapping
const parkDayFields = {
  mkDays: "Magic Kingdom",
  epcotDays: "EPCOT",
  hsDays: "Hollywood Studios",
  akDays: "Animal Kingdom",
  tlDays: "Typhoon Lagoon",
  bbDays: "Blizzard Beach",
};

// === Main Validation Function ===
export const validateField = (field, formData) => {
  if (!formData || typeof formData !== "object") return false;

  // Helper function to get value from nested form data structure
  const getFieldValue = (fieldId) => {
    // Map field IDs to their sections
    const fieldToSection = {
      // Basic Information
      fullName: 'basicInformation',
      email: 'basicInformation', 
      phoneNumber: 'basicInformation',
      startDate: 'basicInformation',
      endDate: 'basicInformation',
      
      // Group Information
      adults: 'groupInfo',
      numAdultMales: 'groupInfo',
      numAdultFemales: 'groupInfo',
      kids: 'groupInfo',
      numBoys: 'groupInfo',
      numGirls: 'groupInfo',
      agesOfBoys: 'groupInfo',
      agesOfGirls: 'groupInfo',
      
      // Ticket & Park Access
      ticketType: 'parkTicketAccess',
      hasParkHopper: 'parkTicketAccess',
      
      // Park Selection
      parks: 'parkVisitPlans',
      mkDays: 'parkVisitPlans',
      hsDays: 'parkVisitPlans',
      epcotDays: 'parkVisitPlans',
      akDays: 'parkVisitPlans',
      tlDays: 'parkVisitPlans',
      bbDays: 'parkVisitPlans',
      includeRestDays: 'parkVisitPlans',
      restDayPlacement: 'parkVisitPlans',
      restDayStyle: 'parkVisitPlans',
      restDayCount: 'parkVisitPlans',
      
      // Accommodation & Transportation
      transportation: 'accommodation',
      stayingOnProperty: 'accommodation',
      resort: 'accommodation',
      otherHotel: 'accommodation',
      
      // Attraction & Ride Preferences
      lightningLane: 'attractionRideStrategy',
      rideStrategy: 'attractionRideStrategy',
      mustRides: 'attractionRideStrategy',
      
      // Shows & Firework Preferences
      showStrategy: 'showFireworkStrategy',
      mustSeeShows: 'showFireworkStrategy',
      
      // Dining Preferences
      diningPlanInterest: 'diningPreferences',
      restaurantStrategy: 'diningPreferences',
      mustTryRestaurants: 'diningPreferences',
      diningTime: 'diningPreferences',
      allergies: 'diningPreferences',
      
      // Budget Preferences
      budget: 'budget',
      budgetLabel: 'budget',
      budgetMin: 'budget',
      budgetMax: 'budget',
      
      // Time & Energy Preferences
      parkStartTime: 'timeEnergy',
      breakTime: 'timeEnergy',
      
      // Special Considerations
      celebration: 'specialConsiderations',
      celebrationDetails: 'specialConsiderations',
      celebrationDate: 'specialConsiderations',
      hasDisability: 'specialConsiderations',
      disabilities: 'specialConsiderations',
      additionalInfo: 'specialConsiderations',
      
      // Specialty/Premium Experiences
      enchantedExperiences: 'specialtyExperiences',
      mustExperience: 'specialtyExperiences'
    };

    const section = fieldToSection[fieldId];
    
    if (section && formData[section]) {
      return formData[section][fieldId] || "";
    }
    
    // Fallback to flat structure for unknown fields
    return formData[fieldId] || "";
  };

  const value = getFieldValue(field.id);

  // Skip info-only fields
  if (field.type === "info") return true;

  // Skip optional fields
  if (optionalIds.includes(field.id)) return true;

  // === CHILD VALIDATION ===
  const totalKids = parseInt(getFieldValue('kids') || "0", 10);
  if (
    totalKids === 0 &&
    ["numBoys", "numGirls", "agesOfBoys", "agesOfGirls"].includes(field.id)
  ) {
    return true;
  }

  // === ADULT VALIDATION ===
  const totalAdults = parseInt(getFieldValue('adults') || "0", 10);
  if (
    totalAdults === 0 &&
    ["numAdultMales", "numAdultFemales"].includes(field.id)
  ) {
    return true;
  }

  // === PARK DAYS ===
  if (parkDayFields[field.id]) {
    const selectedParks = getFieldValue('parks') || [];
    const parkName = parkDayFields[field.id];

    // Only validate if the park was selected
    if (selectedParks.includes(parkName)) {
      const parsed = parseInt(value || "0", 10);
      if (isNaN(parsed) || parsed < 1) {
        toast.error(`Please enter at least 1 day for ${parkName}.`);
        return false;
      }
    }

    return true; // if not selected, skip validation
  }

  // === STAYING ON PROPERTY LOGIC ===
  if (field.id === "resort") {
    if (getFieldValue('stayingOnProperty') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please select your Disney resort.");
      return false;
    }
  }

  if (field.id === "offPropertyHotel") {
    if (getFieldValue('stayingOnProperty') !== "No") return true;
    if (isEmpty(value)) {
      toast.error("Please enter your off-property hotel.");
      return false;
    }
  }

  if (field.id === "otherHotel") {
    const stayingOffProperty = getFieldValue('stayingOnProperty') === "No";
    const selectedOther = getFieldValue('offPropertyHotel') === "Other";
    if (!(stayingOffProperty && selectedOther)) return true;
    if (isEmpty(value)) {
      toast.error("Please enter your specific off-property hotel.");
      return false;
    }
  }

  // === CELEBRATION DETAILS (dropdown) ===
  if (field.id === "celebrationDetails") {
    if (getFieldValue('celebration') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please tell us what you're celebrating.");
      return false;
    }
  }

  // === CELEBRATION DATE ===
  if (field.id === "celebrationDate") {
    if (getFieldValue('celebration') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please provide the date of the celebration.");
      return false;
    }
  }

  // === DISABILITY NOTES ===
  if (field.id === "disabilities") {
    if (getFieldValue('hasDisability') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please explain the disability or mobility concern.");
      return false;
    }
  }

  // === EXPERIENCE SELECTIONS ===
  if (field.id === "mustExperience") {
    const enchantedExperiences = getFieldValue('enchantedExperiences') || [];
    if (!enchantedExperiences.includes("Yes")) return true;
    if (isEmpty(value)) {
      toast.error("Please select at least one premium experience.");
      return false;
    }
  }

  // === TRANSPORTATION ===
  if (field.id === "transportation") {
    if (isEmpty(value)) {
      toast.error("Please select a mode of transportation around the parks.");
      return false;
    }
    return true;
  }


  // === DEFAULT CHECK ===
  if (isEmpty(value)) {
    const label = friendlyLabelMap[field.id] || "this question";
    toast.error(`Please complete: ${label}`);
    return false;
  }

  return true;
};

export const validateAgeDropdowns = (count, list, label) => {
  if (
    count > 0 &&
    (!Array.isArray(list) || list.length !== count || list.includes(""))
  ) {
    toast.error(`Please select the age of each ${label}.`);
    return false;
  }
  return true;
};

export const validateAdultSplit = (formData) => {
  const total = parseInt(formData.groupInfo?.adults || "0");
  const males = parseInt(formData.groupInfo?.numAdultMales || "0");
  const females = parseInt(formData.groupInfo?.numAdultFemales || "0");
  if (males + females !== total) {
    toast.error("Number of adult males and females must equal total adults.");
    return false;
  }
  return true;
};

// ============================================================================
// NOTE: We don't block the form if children distribution doesn't match totalKids.
// Families may enter approximate values (e.g., prefer not to specify gender breakdown,
// have non-binary children, or just provide rough numbers). We treat this as heuristic
// data for planning purposes only. This avoids blocking real families with valid use cases.
// ============================================================================
export const validateKidSplit = (formData) => {
  const total = parseInt(formData.groupInfo?.kids || "0");
  const boys = parseInt(formData.groupInfo?.numBoys || "0");
  const girls = parseInt(formData.groupInfo?.numGirls || "0");
  
  if (total > 0 && boys + girls !== total && (boys > 0 || girls > 0)) {
    // Log warning for analytics/debugging (no PII)
    console.warn('[Questionnaire] Children distribution approximate', {
      total,
      boys,
      girls,
      difference: total - (boys + girls)
    });
    
    // Show informational toast (non-blocking, soft warning)
    toast.info("Note: We'll use approximate age information for planning. Your itinerary will still be personalized!", {
      autoClose: 5000
    });
  }
  
  // Always return true - never block form submission
  return true;
};

export const validateRideSelection = (rides, formData) => {
  const list = Array.isArray(rides) ? rides : [];
  const { filtered, removed } = filterAvailableRides(list, formData || {});

  if (removed.length > 0) {
    return {
      isValid: true,
      filteredRides: filtered,
      message: "Some selections were removed because they're not available for your trip dates."
    };
  }
  if (filtered.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one ride to continue."
    };
  }
  return { isValid: true };
};

export const validateShowSelection = (shows, optedOut, formData) => {
  if (optedOut) {
    return { isValid: true };
  }
  const list = Array.isArray(shows) ? shows : [];
  const { filtered, removed } = filterAvailableShows(list, formData || {});

  if (removed.length > 0) {
    return {
      isValid: true,
      filteredShows: filtered,
      message: "Some selections were removed because they're not available for your trip dates."
    };
  }
  if (filtered.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one show or choose 'Not Interested' to continue."
    };
  }
  return { isValid: true };
};

export const validateCelebrationDetails = (celebration, details) => {
  if (celebration === "Yes" && isEmpty(details)) {
    toast.error("Please tell us what you're celebrating.");
    return false;
  }
  return true;
};

export const validateDisabilityNotes = (hasDisability, note) => {
  if (hasDisability === "Yes" && isEmpty(note)) {
    toast.error("Please explain the disability or mobility concern.");
    return false;
  }
  return true;
};

/**
 * Validates rest-day questions in parkVisitPlans.
 * - includeRestDays is required (must be "Yes" or "No").
 * - If "Yes", restDayCount, restDayPlacement, and restDayStyle are required.
 * @param {Object} formData - Form data with parkVisitPlans
 * @returns {{ isValid: boolean, message?: string }}
 */
export const validateRestDays = (formData) => {
  const pvp = formData?.parkVisitPlans || {};
  const includeRest = (pvp.includeRestDays || "").trim();

  if (includeRest !== "Yes" && includeRest !== "No") {
    return { isValid: false, message: "Please answer whether you would like to include Rest days in your trip." };
  }

  if (includeRest === "No") {
    return { isValid: true };
  }

  if (!(pvp.restDayCount || "").trim()) {
    return { isValid: false, message: "Please select how many rest days you would like." };
  }
  if (!(pvp.restDayPlacement || "").trim()) {
    return { isValid: false, message: "Please select where you would like your Rest day(s) to fall." };
  }
  if (!(pvp.restDayStyle || "").trim()) {
    return { isValid: false, message: "Please select what a Rest day(s) should look like." };
  }

  return { isValid: true };
};

export const validateRestaurantSelection = (restaurants, optedOut) => {
  if (optedOut) {
    return { isValid: true };
  }

  if (!restaurants || restaurants.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one restaurant you'd like to try."
    };
  }

  if (restaurants.length > 20) {
    return {
      isValid: false,
      message: "Please select no more than 20 restaurants."
    };
  }

  return { isValid: true };
};

export const validateExperienceSelection = (experiences, interested) => {
  if (!interested || !interested.includes("Yes")) {
    return { isValid: true };
  }

  if (!experiences || experiences.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one premium experience you'd like to try."
    };
  }

  if (experiences.length > 15) {
    return {
      isValid: false,
      message: "Please select no more than 15 premium experiences."
    };
  }

  return { isValid: true };
};